import { useState } from 'react'
import { useFinancialPlanStore, Expense } from '../../store/financialPlanStore'
import { Plus, Trash2, Receipt } from 'lucide-react'

export function ExpensesStep() {
  const { currentPlan, addExpense, removeExpense } = useFinancialPlanStore()
  const [showForm, setShowForm] = useState(false)
  
  const [newExpense, setNewExpense] = useState<Omit<Expense, 'id'>>({
    category: 'Living',
    amount: 0,
    inflationRate: 2.5,
  })
  
  if (!currentPlan) return null
  
  const handleAdd = () => {
    if (newExpense.amount > 0) {
      addExpense(newExpense)
      setNewExpense({
        category: 'Living',
        amount: 0,
        inflationRate: 2.5,
      })
      setShowForm(false)
    }
  }
  
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value)
  
  const totalExpenses = currentPlan.expenses.reduce((sum, exp) => sum + exp.amount, 0)
  
  const categoryDefaults = {
    'Housing': 2.5,
    'Healthcare': 6.5,
    'Living': 2.5,
    'Discretionary': 2.5,
    'Education': 5.5,
    'Charitable': 2.5,
    'Other': 2.5,
  }
  
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          💡 <strong>Tip:</strong> Healthcare typically inflates at 6-7%, education at 5-6%, while general expenses at 2.5-3%.
          We've preset recommended inflation rates for each category.
        </p>
      </div>
      
      <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-6">
        <p className="text-sm font-medium text-red-900 mb-1">Total Annual Expenses</p>
        <p className="text-3xl font-bold text-red-900">{formatCurrency(totalExpenses)}</p>
      </div>
      
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg flex items-center">
          <Receipt className="h-5 w-5 mr-2 text-primary-600" />
          Annual Expenses
        </h3>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn-primary text-sm flex items-center">
            <Plus className="h-4 w-4 mr-1" />
            Add Expense
          </button>
        )}
      </div>
      
      <div className="space-y-3">
        {currentPlan.expenses.map(expense => (
          <div key={expense.id} className="card border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center">
                  <h4 className="font-medium">{expense.category}</h4>
                  <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                    {expense.inflationRate}% inflation
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Annual Amount</p>
                    <p className="font-medium">{formatCurrency(expense.amount)}</p>
                  </div>
                  {expense.startAge && (
                    <div>
                      <p className="text-xs text-gray-500">Start Age</p>
                      <p className="font-medium">{expense.startAge}</p>
                    </div>
                  )}
                  {expense.endAge && (
                    <div>
                      <p className="text-xs text-gray-500">End Age</p>
                      <p className="font-medium">{expense.endAge}</p>
                    </div>
                  )}
                </div>
              </div>
              <button onClick={() => removeExpense(expense.id)} className="text-red-600 hover:text-red-700 ml-4">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {showForm && (
        <div className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50">
          <h4 className="font-medium mb-4">Add Expense Category</h4>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Category *</label>
                <select
                  value={newExpense.category}
                  onChange={(e) => {
                    const category = e.target.value as Expense['category']
                    setNewExpense({
                      ...newExpense,
                      category,
                      inflationRate: categoryDefaults[category] || 2.5
                    })
                  }}
                  className="input-field"
                >
                  <option value="Housing">Housing (mortgage, rent, utilities, maintenance)</option>
                  <option value="Healthcare">Healthcare (premiums, out-of-pocket, LTC)</option>
                  <option value="Living">Living Expenses (food, transportation, etc.)</option>
                  <option value="Discretionary">Discretionary (travel, entertainment, hobbies)</option>
                  <option value="Education">Education (college costs)</option>
                  <option value="Charitable">Charitable Giving</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="label">Annual Amount *</label>
                <input
                  type="number"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({ ...newExpense, amount: parseFloat(e.target.value) || 0 })}
                  className="input-field"
                  min="0"
                  step="1000"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Inflation Rate (%)</label>
                <input
                  type="number"
                  value={newExpense.inflationRate}
                  onChange={(e) => setNewExpense({ ...newExpense, inflationRate: parseFloat(e.target.value) || 0 })}
                  className="input-field"
                  step="0.5"
                />
                <p className="text-xs text-gray-500 mt-1">Recommended preset for category</p>
              </div>
              <div>
                <label className="label">Start Age (Optional)</label>
                <input
                  type="number"
                  value={newExpense.startAge || ''}
                  onChange={(e) => setNewExpense({ ...newExpense, startAge: parseInt(e.target.value) || undefined })}
                  className="input-field"
                  min="0"
                  max="100"
                  placeholder="Leave blank for now"
                />
              </div>
              <div>
                <label className="label">End Age (Optional)</label>
                <input
                  type="number"
                  value={newExpense.endAge || ''}
                  onChange={(e) => setNewExpense({ ...newExpense, endAge: parseInt(e.target.value) || undefined })}
                  className="input-field"
                  min="0"
                  max="100"
                  placeholder="Leave blank if ongoing"
                />
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button onClick={handleAdd} className="btn-primary">Add Expense</button>
              <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
      
      {currentPlan.expenses.length === 0 && !showForm && (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <Receipt className="h-12 w-12 mx-auto mb-2 opacity-30" />
          <p>No expenses added yet</p>
          <p className="text-sm mt-1">Add your major expense categories</p>
        </div>
      )}
    </div>
  )
}
